import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiError,
  clearToken,
  getToken,
  SESSION_EXPIRED,
  signOut,
  runningVersion,
  setToken,
  timeAgo,
  type Deployment,
  type Identity,
  type RestaurantMember,
  type PortalUser,
  type Restaurant,
} from './api';
import { ConnectPanel } from './ConnectPanel';
import { ScandeerConnectPanel } from './ScandeerConnectPanel';
import { WhatsAppConnectPanel } from './WhatsAppConnectPanel';
import { LandingPage } from './LandingPage';
import { COUNTRIES, DEFAULT_COUNTRY, countryByIso } from './countries';
import { ANDROID_PLAY_URL, WINDOWS_INSTALLER_URL, WINDOWS_INSTALLER_VERSION } from './installLinks';
import {
  SignInMethodsDrawer,
  WhatsAppMark,
  WhatsAppQrPanel,
  type GoogleLinkReturn,
} from './WhatsAppLogin';

// Central Portal SPA Application - Fleet Management & Restaurant Onboarding
export function App() {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [booting, setBooting] = useState(true);
  const [oauthError, setOauthError] = useState('');
  const [forceDashboard, setForceDashboard] = useState(false);
  /** Proof from a Google re-confirm, handed to the account drawer. */
  const [passwordConfirm, setPasswordConfirm] = useState<
    { kind: 'ok'; token: string } | { kind: 'error'; reason: string } | null
  >(null);
  const [googleReturn, setGoogleReturn] = useState<GoogleLinkReturn | null>(null);

  const isDashboardHost =
    window.location.hostname.startsWith('dashboard.') ||
    window.location.pathname.startsWith('/portal') ||
    window.location.pathname.startsWith('/dashboard') ||
    window.location.hash.includes('token') ||
    window.location.hash.includes('oauth_error') ||
    window.location.hash.includes('google_link') ||
    window.location.hash.includes('password_confirm') ||
    forceDashboard;

  // The Google callback redirects to /#token=… or /#oauth_error=…. A fragment
  // rather than a query string because a fragment never reaches a server, so
  // the session cannot end up in an access log.
  useEffect(() => {
    const hash = new URLSearchParams(location.hash.slice(1));
    const token = hash.get('token');
    const err = hash.get('oauth_error');
    if (token) {
      setToken(token);
      setForceDashboard(true);
    }
    if (err) {
      setOauthError(err);
      setForceDashboard(true);
    }
    // Back from linking Google to an account already signed in.
    const linked = hash.get('google_link');
    const linkErr = hash.get('google_link_error');
    const conflict = hash.get('google_link_conflict');
    if (linked) setGoogleReturn({ kind: 'linked', email: linked });
    if (linkErr) setGoogleReturn({ kind: 'error', reason: linkErr });
    if (conflict) setGoogleReturn({ kind: 'conflict', token: conflict });
    if (linked || linkErr || conflict) setForceDashboard(true);

    // Back from re-confirming with Google before setting a password. The
    // token is proof, not a session, and it is good for five minutes — the
    // same fragment channel as above, for the same reason.
    const confirmed = hash.get('password_confirm');
    const confirmErr = hash.get('password_confirm_error');
    if (confirmed) setPasswordConfirm({ kind: 'ok', token: confirmed });
    if (confirmErr) setPasswordConfirm({ kind: 'error', reason: confirmErr });
    if (confirmed || confirmErr) setForceDashboard(true);

    if (token || err || linked || linkErr || conflict || confirmed || confirmErr) {
      history.replaceState(null, '', location.pathname);
    }
  }, []);

  /*
   * Pick the session up again, from whichever half of it this tab has.
   *
   * The token lives in sessionStorage, which is per TAB — so a second tab,
   * opened by pasting the URL rather than clicking a link, has none. It used
   * to stop there and show the sign-in screen, while a perfectly good session
   * sat in the same browser: the server also accepts an `sdportal_session`
   * cookie, twelve hours long, and every request here already carries it
   * because same-origin fetches send cookies by default.
   *
   * So with no token, ask anyway. The cookie answers or it does not.
   *
   * A RAW fetch for that probe, never api.me(): the shared client reloads the
   * page on a 401, and a probe whose whole purpose is to find out IF we are
   * signed in would turn a signed-out visit into a reload loop.
   */
  /*
   * Back to the sign-in screen when the session lapses.
   *
   * Dispatched by the API client on any 401. Identity going null is what
   * unmounts the fleet — precisely, and without the reload that used to race
   * the screens it was meant to replace.
   */
  useEffect(() => {
    const onExpired = () => setIdentity(null);
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, []);

  useEffect(() => {
    let live = true;

    if (getToken()) {
      void api
        .me()
        .then((r) => live && setIdentity(r.identity))
        .catch(() => clearToken())
        .finally(() => live && setBooting(false));
      return () => {
        live = false;
      };
    }

    void fetch('/api/me')
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (live && b?.identity) setIdentity(b.identity);
      })
      .catch(() => {})
      .finally(() => live && setBooting(false));

    return () => {
      live = false;
    };
  }, []);

  // The cold start. A bare ring on an empty page gave no sign of WHAT was
  // starting; this is the front door of the product, so it says so.
  if (booting) {
    return (
      <div className="bootscreen">
        <div className="bootmark" role="status" aria-label="Starting Billdedo">
          <img src="/favicon-180.png" alt="" width={54} height={54} />
        </div>
        <span className="bootscreen-text">Starting Billdedo…</span>
      </div>
    );
  }

  // The site root is the marketing page, whoever is asking. Gating it on a
  // missing identity made it unreachable the moment a session existed: the
  // header's Website link lands back here, the stored token restores identity,
  // and the console renders again. Signed in or not, `/` is the website and
  // `/portal` is the console.
  if (!isDashboardHost) {
    return (
      <LandingPage
        signedIn={!!identity}
        onLogin={() => {
          // Same origin, one level down — no second hostname to provision and
          // nothing for the zone's wildcard route to swallow. history.pushState
          // rather than a navigation so the already-loaded SPA just re-renders.
          window.history.pushState({}, '', '/portal');
          setForceDashboard(true);
        }}
      />
    );
  }

  if (!identity) {
    return (
      <Login
        onDone={setIdentity}
        oauthError={oauthError}
        onBackToHome={() => setForceDashboard(false)}
      />
    );
  }

  return (
    <Fleet
      identity={identity}
      googleReturn={googleReturn}
      passwordConfirm={passwordConfirm}
      onSignOut={() => {
        // The cookie too, not just this tab's copy — see signOut(). Without
        // that, the boot probe above would find the session still live and
        // sign the operator back in on the next load.
        void signOut();
        setIdentity(null);
      }}
    />
  );
}

/** `wa-919876543210@whatsapp.invalid` reads as `+919876543210`. */
function displayLogin(email: string): string {
  const m = /^wa-(\d+)@whatsapp\.invalid$/.exec(email);
  return m ? `+${m[1]}` : email;
}

// ---------------------------------------------------------------------------

const OAUTH_MESSAGES: Record<string, string> = {
  cancelled: 'Google sign-in was cancelled.',
  no_account:
    'That Google account has no access to this portal. Ask an administrator to add you.',
  account_disabled:
    'That account has been deactivated. Ask an administrator to switch it back on.',
  email_unverified: 'That Google account has no verified email address.',
  state_mismatch: 'The sign-in link expired or was tampered with. Try again.',
  state_missing: 'The sign-in link expired. Try again.',
  exchange_failed: 'Google could not complete the sign-in. Try again.',
};

function Login({
  onDone,
  oauthError,
  onBackToHome,
}: {
  onDone: (i: Identity) => void;
  oauthError: string;
  onBackToHome?: () => void;
}) {
  const [methods, setMethods] = useState<
    { password?: boolean; google: boolean; whatsapp?: boolean } | null
  >(null);
  // The WhatsApp QR takes the card over while it is up; Cancel gives it back.
  const [whatsapp, setWhatsapp] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.authMethods().then(setMethods).catch(() => setMethods({ google: false }));
  }, []);

  /*
   * Nobody can get a FIRST session here.
   *
   * A password is only ever added from inside a session proved with Google or
   * WhatsApp, so a portal with neither configured cannot issue the session
   * that would let anyone set one. The password field alone is a dead end,
   * and saying so beats showing a form that can never succeed.
   */
  const noFirstDoor = methods !== null && !methods.google && !methods.whatsapp;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api.login(identifier.trim(), password);
      setToken(r.token);
      onDone(r.identity);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the portal');
      setPassword('');
      setBusy(false);
    }
  }

  return (
    <div className="centre">
      <div className="card login">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0 }}>BILLDEDO</h1>
          {onBackToHome && (
            <button
              type="button"
              className="btn sm ghost"
              style={{ fontSize: 12 }}
              onClick={onBackToHome}
            >
              ← Home
            </button>
          )}
        </div>
        <p className="sub">Deploy and manage restaurants</p>

        {oauthError && (
          <div className="banner bad">
            {OAUTH_MESSAGES[oauthError] ?? `Google sign-in failed (${oauthError}).`}
          </div>
        )}
        {error && <div className="banner bad">{error}</div>}

        {whatsapp ? (
          <WhatsAppQrPanel
            start={api.whatsappStart}
            onCancel={() => setWhatsapp(false)}
            onResult={(r) => {
              if (r.status === 'signed_in') {
                setToken(r.token);
                onDone(r.identity);
              }
            }}
          />
        ) : (
          <>
            {methods?.google && (
              <a className="btn google" href="/api/auth/google">
                <GoogleMark />
                Sign in with Google
              </a>
            )}
            {methods?.whatsapp && (
              <button type="button" className="btn google" onClick={() => setWhatsapp(true)}>
                <WhatsAppMark />
                Sign in with WhatsApp
              </button>
            )}
            {noFirstDoor && (
              <div className="banner bad">
                Neither Google nor WhatsApp sign-in is switched on for this portal, so no
                new session can be started and no password can be set. Ask an
                administrator to configure one of them.
              </div>
            )}

            {/* The divider only earns its place when there is something above
                it to divide from. */}
            {methods?.password && (methods?.google || methods?.whatsapp) && (
              <div className="divider"><span>or</span></div>
            )}

            {methods?.password && (
              <form onSubmit={submit} style={{ display: 'contents' }}>
                {/* One field for both, because an account created by WhatsApp
                    has no real email to type — only the number it was made
                    with. Asking people to choose first would mean explaining
                    that. */}
                <label htmlFor="who">Email or phone</label>
                <input
                  id="who"
                  type="text"
                  value={identifier}
                  onChange={(ev) => setIdentifier(ev.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />

                <label htmlFor="p">Password or PIN</label>
                <input
                  id="p"
                  type="password"
                  value={password}
                  onChange={(ev) => setPassword(ev.target.value)}
                  autoComplete="current-password"
                  required
                />

                <button className="btn primary lg" disabled={busy}>
                  {busy ? 'Signing in…' : 'Sign in'}
                </button>

                <p className="sub" style={{ margin: '10px 0 0', fontSize: 12, textAlign: 'center' }}>
                  No password yet? Sign in above, then set one from your account menu.
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Google's mark, inline so the CSP needs no external host. */
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.5h12c-.2 2-1.6 5-4.5 7l6.9 5.3C42.5 36.2 45 30.6 45 24z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.6-5.4l-6.9-5.3c-1.9 1.3-4.4 2.2-7.7 2.2-5.9 0-10.9-3.9-12.7-9.2l-7.1 5.5C7.7 41 15.3 46 24 46z" />
      <path fill="#FBBC05" d="M11.3 28.3A13.5 13.5 0 0 1 10.6 24c0-1.5.3-3 .7-4.3l-7.1-5.5A22 22 0 0 0 2 24c0 3.5.8 6.9 2.2 9.8z" />
      <path fill="#EA4335" d="M24 10.2c3.3 0 6.2 1.1 8.5 3.3l6.1-6.1C34.9 3.9 30 2 24 2 15.3 2 7.7 7 4.2 14.2l7.1 5.5C13.1 14.1 18.1 10.2 24 10.2z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------

function getRoleBadge(identity: Identity): { label: string; cls: string } {
  if (identity.kind === 'scandeer' && identity.role === 'admin') {
    return { label: 'Super Admin', cls: 'pill role-super-admin' };
  }
  if (identity.kind === 'scandeer' || identity.kind === 'reseller') {
    return { label: 'Admin', cls: 'pill role-admin' };
  }
  return { label: 'User (Restaurant Owner)', cls: 'pill role-user' };
}

function Fleet({
  identity,
  onSignOut,
  googleReturn,
  passwordConfirm,
}: {
  identity: Identity;
  onSignOut: () => void;
  googleReturn?: GoogleLinkReturn | null;
  /** Proof from a Google re-confirm, on its way to the sign-in drawer. */
  passwordConfirm?: { kind: 'ok'; token: string } | { kind: 'error'; reason: string } | null;
}) {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  /* True until the first load settles. An empty list means "none" only once
     we have asked — before that the pane claimed RESTAURANTS 0 and offered
     to add the first one to an owner who already has several. */
  const [listLoading, setListLoading] = useState(true);
  const [latestVersion, setLatestVersion] = useState<string>('1.0.1');
  const [selected, setSelected] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  // Opens by itself when Google has just sent the browser back from a link.
  const [showSignIn, setShowSignIn] = useState(!!googleReturn);
  const [error, setError] = useState('');

  const reload = useCallback(async () => {
    try {
      const r = await api.restaurants();
      setRestaurants(r.restaurants);
      if (r.latest_version) setLatestVersion(r.latest_version);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load restaurants');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /**
   * Follow a deploy while it runs.
   *
   * A deploy takes minutes and the page only ever loaded its status once, so
   * the pill sat on "provisioning" until somebody pressed F5 — and whether it
   * had finished or failed was a question the screen could not answer.
   *
   * The poll exists only while something is actually deploying, so a portal
   * left open on a quiet fleet makes no requests at all.
   */
  const anyDeploying = restaurants.some((r) => r.status === 'provisioning');
  useEffect(() => {
    if (!anyDeploying) return;
    const id = setInterval(() => void reload(), 5000);
    return () => clearInterval(id);
  }, [anyDeploying, reload]);

  const current = restaurants.find((r) => r.id === selected) ?? null;
  const roleBadge = getRoleBadge(identity);
  const isSuperAdmin = identity.kind === 'scandeer' && identity.role === 'admin';

  return (
    <div className="shell">
      <header className="bar">
        <img className="brand-logo" src="/favicon-180.png" alt="" width={26} height={26} />
        <strong className="brand">BILLDEDO</strong>
        <span className={roleBadge.cls}>{roleBadge.label}</span>
        <span className="pill version-pill">Latest v{latestVersion}</span>
        <span className="spacer" />
        {/*
          One class rather than the same inline border and margin repeated on
          each. They are peers and should look it — the header read as three
          unrelated controls that happened to sit together.
        */}
        <a href="/" className="bar-btn">
          Website
        </a>
        {/* Beside Website, so it is reachable from every page without
            hunting — including the list, where somebody is about to press
            Add restaurant. */}
        <a href={SETUP_GUIDE_URL} target="_blank" rel="noreferrer" className="bar-btn">
          Setup guide
        </a>
        {isSuperAdmin && (
          <>
            <button
              className="bar-btn"
              onClick={() => setShowPayment(true)}
              title="Super Admin only — Razorpay gateway credentials"
            >
              Payment Gateway
            </button>
            <button className="bar-btn" onClick={() => setShowUsers(true)}>
              Users &amp; Roles
            </button>
          </>
        )}
        <span className="bar-sep" />
        {/* An account WhatsApp created has a placeholder email; the phone
            is the thing its owner recognises. */}
        <span className="hint">{displayLogin(identity.email)}</span>
        <button className="bar-btn" onClick={() => setShowSignIn(true)}>
          Sign-in methods
        </button>
        <button className="bar-btn" onClick={onSignOut}>
          Sign out
        </button>
      </header>

      <div className="panes">
        <aside className="pane">
          <div className="pane-head">
            <h2>Restaurants</h2>
            <span className="count">{listLoading ? '—' : restaurants.length}</span>
            <span className="spacer" />
            {identity.role !== 'readonly' && (
              <button className="btn sm primary" onClick={() => setAdding(true)}>
                + Add
              </button>
            )}
          </div>

          {error && <div className="banner bad">{error}</div>}

          {listLoading ? (
            <p className="empty loading-row">
              <span className="spinner sm" aria-hidden /> Loading restaurants…
            </p>
          ) : (
            !restaurants.length && (
              <p className="empty">
                No restaurants yet. Add one, then connect its Cloudflare account.
              </p>
            )
          )}

          <ul className="list">
            {restaurants.map((r) => {
              // What it is running, not what we once recorded shipping.
              const running = runningVersion(r);
              const isDeployed = !!r.live_at && !!running.version;
              const runningLabel = running.version;
              const isUpToDate = isDeployed && runningLabel === latestVersion;

              return (
                <li key={r.id}>
                  <button
                    className={`row ${r.id === selected ? 'on' : ''}`}
                    onClick={() => setSelected(r.id)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <span className="name">{r.name}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginTop: 2 }}>
                      <span className="meta" style={{ margin: 0 }}>
                        {r.slug}
                      </span>
                      {isDeployed ? (
                        isUpToDate ? (
                          <span className="version-tag up-to-date" title="Up to date">
                            v{runningLabel} ✓
                          </span>
                        ) : (
                          <span className="version-tag update-available" title={`Update available: v${latestVersion}`}>
                            v{runningLabel} ➜ v{latestVersion}
                          </span>
                        )
                      ) : (
                        <span className="version-tag unreleased">Not deployed</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span className="pill" style={{ fontSize: 10, padding: '1px 6px', background: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderColor: 'rgba(56,189,248,0.25)' }}>
                        ₹1,000/mo
                      </span>
                      {r.plan_status === 'expired' ? (
                        <span className="pill" style={{ fontSize: 10, padding: '1px 6px', background: 'rgba(239,68,68,0.15)', color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>
                          🔒 Expired
                        </span>
                      ) : r.plan_status === 'trial' ? (
                        <span className="pill" style={{ fontSize: 10, padding: '1px 6px', background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.25)' }}>
                          7d Trial ({r.days_remaining ?? 7}d)
                        </span>
                      ) : (
                        <span className="pill" style={{ fontSize: 10, padding: '1px 6px', background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderColor: 'rgba(34,197,94,0.25)' }}>
                          Active ({r.days_remaining}d)
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <main className="pane detail">
          {current ? (
            <Detail
              restaurant={current}
              identity={identity}
              latestVersion={latestVersion}
              onChanged={reload}
              onDeleted={() => setSelected(null)}
            />
          ) : (
            <div className="placeholder">
              <p>Pick a restaurant.</p>
            </div>
          )}
        </main>
      </div>

      {adding && (
        <AddRestaurant
          onClose={() => setAdding(false)}
          onAdded={async (id) => {
            setAdding(false);
            await reload();
            setSelected(id);
          }}
        />
      )}

      {showSignIn && (
        <SignInMethodsDrawer
          googleReturn={googleReturn}
          passwordConfirm={passwordConfirm}
          onClose={() => setShowSignIn(false)}
        />
      )}
      {showUsers && (
        <UsersDialog
          onClose={() => setShowUsers(false)}
          currentUserId={identity.id}
        />
      )}

      {showPayment && (
        <PaymentSettingsDialog
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}

/**
 * What the last few deploys did, and what the failed one said.
 *
 * The portal has always recorded this — every deploy writes its state and its
 * error to the deployments table, and GET /api/restaurants/:id returns them —
 * and nothing had ever rendered it. A failed deploy showed as a status pill
 * and nothing else, so the only way to learn why was to open the Actions run
 * on GitHub, which needs an account on the repository that most people
 * running this portal do not have.
 *
 * Polls while a deploy is in flight, for the same reason the list does.
 */
function DeployHistory({ restaurantId, live }: { restaurantId: string; live: boolean }) {
  const [rows, setRows] = useState<Deployment[] | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.restaurant(restaurantId);
      setRows(r.deployments ?? []);
    } catch {
      // The detail pane above already reports a portal that cannot be reached.
    }
  }, [restaurantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => void load(), 5000);
    return () => clearInterval(id);
  }, [live, load]);

  if (!rows || rows.length === 0) return null;

  const when = (t: number | null) =>
    t ? new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';

  const took = (d: Deployment) =>
    d.finished_at && d.started_at
      ? `${Math.max(1, Math.round((d.finished_at - d.started_at) / 1000))}s`
      : '';

  return (
    <section className="panel" style={{ marginTop: 24 }}>
      <div className="panel-head">
        <h2>Deploys</h2>
      </div>
      <div style={{ padding: '4px 0' }}>
        {rows.slice(0, 5).map((d) => {
          const failed = d.state === 'failed';
          const running = d.state === 'queued' || d.state === 'claimed' || d.state === 'running';
          return (
            <div
              key={d.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
                padding: '9px 0',
                borderTop: '1px solid var(--border, #e5e7eb)',
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: 12.5,
                  color: failed ? '#dc2626' : running ? '#b45309' : '#15803d',
                }}
              >
                {failed ? 'failed' : running ? d.state : 'succeeded'}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--text-2, #4b5563)' }}>{d.kind}</span>
              <span style={{ fontSize: 12.5, color: 'var(--text-3, #6b7280)' }}>
                {when(d.started_at)} {took(d) && `· ${took(d)}`}
              </span>
              <span style={{ flex: 1 }} />
              {d.runner_url && (
                <a
                  href={d.runner_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12.5 }}
                >
                  Full log
                </a>
              )}
              {/* The whole point of the panel. */}
              {d.error && (
                <div
                  style={{
                    flexBasis: '100%',
                    fontSize: 12.5,
                    color: '#b91c1c',
                    background: 'rgba(220,38,38,0.07)',
                    border: '1px solid rgba(220,38,38,0.25)',
                    borderRadius: 6,
                    padding: '7px 10px',
                    marginTop: 4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {d.error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * The setup guide, served from this portal's own assets.
 *
 * One constant because it is linked from three places — the landing page, a
 * restaurant's page, and the new-restaurant dialog — and a fourth lives in the
 * POS dashboard pointing at this same file by absolute URL.
 */
export const SETUP_GUIDE_URL = '/setup-guide.pdf';

/** The link itself, so every one of them reads and behaves the same. */
export function SetupGuideLink({ style }: { style?: React.CSSProperties }) {
  return (
    <a
      href={SETUP_GUIDE_URL}
      target="_blank"
      rel="noreferrer"
      style={{ fontWeight: 650, ...style }}
    >
      Read the setup guide (PDF)
    </a>
  );
}

/**
 * Who else is on this restaurant.
 *
 * Two roles, and the difference is the whole point: an admin gets the till the
 * way the owner has it, a cashier gets the floor and the bill history and no
 * way to change anything. The role is asserted to the restaurant at sign-in,
 * so this list is what the till enforces, not a note about it.
 */
function MembersPanel({
  restaurantId,
  onError,
}: {
  restaurantId: string;
  onError: (m: string) => void;
}) {
  const [data, setData] = useState<{
    owner: RestaurantMember | null;
    members: RestaurantMember[];
    canManage: boolean;
  } | null>(null);
  // Add by Google sign-in address or by WhatsApp number — whichever the
  // person actually uses to sign in.
  const [by, setBy] = useState<'google' | 'phone'>('google');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<'admin' | 'cashier'>('cashier');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const dial = countryByIso(country).dial;
  const ready = by === 'google' ? !!email.trim() : !!phone.replace(/\D/g, '');

  const load = useCallback(async () => {
    try {
      setData(await api.members(restaurantId));
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not load the people on this restaurant');
    }
  }, [restaurantId, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!data) return null;

  const rows = [...(data.owner ? [data.owner] : []), ...data.members];

  return (
    <section className="panel" style={{ marginTop: 24 }}>
      <div className="panel-head">
        <h2>People</h2>
      </div>

      <p className="sub" style={{ margin: '4px 0 12px' }}>
        An <strong>admin</strong> gets the whole till — settings, reports, menu and
        staff. A <strong>cashier</strong> gets the floor and the bill history, and
        cannot change anything. Everyone here signs in with Google or WhatsApp;
        there is no password to hand out.
      </p>

      {note && <div className="banner ok">{note}</div>}

      <div style={{ padding: '2px 0' }}>
        {rows.map((m) => (
          <div
            key={m.user_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '9px 0',
              borderTop: '1px solid var(--line)',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 650, fontSize: 13.5 }}>{m.name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                {/* A placeholder address is not something anyone chose — show
                    the number instead, and the real address alongside it. */}
                {[displayLogin(m.email).startsWith('+') ? null : m.email, m.phone ? `+${m.phone}` : null]
                  .filter(Boolean)
                  .join(' · ') || m.email}
              </div>
            </div>
            <span className="spacer" style={{ flex: 1 }} />
            <span className="pill">{m.role}</span>
            {/* The owner is not a membership and cannot be removed here —
                they are the person whose Cloudflare account this runs in. */}
            {data.canManage && m.role !== 'owner' && (
              <button
                className="btn sm"
                disabled={busy}
                onClick={async () => {
                  if (!confirm(`Remove ${m.name} from this restaurant?`)) return;
                  setBusy(true);
                  setNote('');
                  try {
                    const r = await api.removeMember(restaurantId, m.user_id);
                    setNote(r.message);
                    await load();
                  } catch (e) {
                    onError(e instanceof ApiError ? e.message : 'Could not remove them');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {data.canManage && (
        <form
          style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setNote('');
            try {
              if (by === 'google') {
                await api.addMember(restaurantId, { email: email.trim(), role });
                setEmail('');
                setNote('Added. They sign in at this portal with Google.');
              } else {
                await api.addMember(restaurantId, { countryCode: dial, phone: phone.trim(), role });
                setPhone('');
                setNote(
                  'Added. They sign in at this portal with WhatsApp, from this number.',
                );
              }
              await load();
            } catch (err) {
              onError(err instanceof ApiError ? err.message : 'Could not add them');
            } finally {
              setBusy(false);
            }
          }}
        >
          <div className="seg" role="radiogroup" aria-label="Add by">
            <button
              type="button"
              role="radio"
              aria-checked={by === 'google'}
              className={by === 'google' ? 'on' : ''}
              onClick={() => setBy('google')}
            >
              Google
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={by === 'phone'}
              className={by === 'phone' ? 'on' : ''}
              onClick={() => setBy('phone')}
            >
              Phone
            </button>
          </div>
          {by === 'google' ? (
            <input
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Their Google sign-in address"
              style={{ flex: '1 1 240px', minWidth: 0 }}
              aria-label="Email address to add"
            />
          ) : (
            <div style={{ display: 'flex', gap: 6, flex: '1 1 300px', minWidth: 0 }}>
              <select
                className="input"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                style={{ flex: '0 0 150px', minWidth: 0 }}
                aria-label="Country"
              >
                {/* India first, then everyone alphabetically — it is nearly
                    every number added here. */}
                <option value={DEFAULT_COUNTRY}>India (+91)</option>
                <option disabled>──────────</option>
                {COUNTRIES.filter((c) => c.iso !== DEFAULT_COUNTRY).map((c) => (
                  <option key={c.iso} value={c.iso}>
                    {c.name} (+{c.dial})
                  </option>
                ))}
              </select>
              <input
                className="input"
                type="tel"
                inputMode="numeric"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={dial === '91' ? '98765 43210' : 'Phone number'}
                style={{ flex: 1, minWidth: 0 }}
                aria-label={`WhatsApp number, without +${dial}`}
              />
            </div>
          )}
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as 'admin' | 'cashier')}
            style={{ flex: '0 0 130px' }}
            aria-label="Role"
          >
            <option value="cashier">Cashier</option>
            <option value="admin">Admin</option>
          </select>
          <button className="btn primary" disabled={busy || !ready}>
            {busy ? 'Working…' : 'Add'}
          </button>
        </form>
      )}
    </section>
  );
}

function StatusPill({ status }: { status: Restaurant['status'] }) {
  return <span className={`pill status-${status}`}>{status}</span>;
}

// ---------------------------------------------------------------------------

function Detail({
  restaurant,
  identity,
  latestVersion,
  onChanged,
  onDeleted,
}: {
  restaurant: Restaurant;
  identity: Identity;
  latestVersion: string;
  onChanged: () => Promise<void>;
  /** The record this pane renders has gone; the pane has to go with it. */
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const canEdit = identity.role !== 'readonly';
  const connected = restaurant.has_token > 0 && !!restaurant.cf_account_id;
  const deploying = restaurant.status === 'provisioning';

  const [opening, setOpening] = useState<string | null>(null);
  /** A health probe is in flight for this restaurant. */
  const [checking, setChecking] = useState(false);
  /** Typed back before the clear-data button will enable. */
  const [clearConfirm, setClearConfirm] = useState('');
  /** Typed back before the delete button will enable. */
  const [deleteConfirm, setDeleteConfirm] = useState('');

  /*
   * A cashier is not here to run the restaurant.
   *
   * Everything else on this page is the restaurant's administration — the
   * Cloudflare account, deploys, the plan and the money, clearing the trade.
   * A cashier has no business with any of it, and the server agrees: their
   * assertion says `cashier`, so the till would refuse it even if the button
   * were here to press.
   *
   * They still need the page, because the portal is how they get into the
   * till at all — a member has no password, only Google.
   */
  const viewerIsCashier = restaurant.my_role === 'cashier';

  // The restaurant's own answer beats the deploy pipeline's record. See
  // runningVersion() in api.ts for why those are two different things.
  const running = runningVersion(restaurant);
  const isDeployed = !!restaurant.live_at && !!running.version;
  const runningLabel = running.version;
  const isUpToDate = isDeployed && runningLabel === latestVersion;

  async function openRestaurantApp(path: '' | 'pos' | 'captain' = '') {
    if (!restaurant.base_url) return;
    setOpening(path || 'dashboard');
    setErr('');
    try {
      // 1. Obtain signed SSO assertion from Central Portal
      const sess = await api.createSession(restaurant.id);
      const baseUrl = sess.baseUrl.replace(/\/$/, '');

      // 2. Open directly in target tab with #assertion=...
      // The restaurant dashboard/POS SPA exchanges it locally on its own origin
      const targetUrl = `${baseUrl}/${path ? path.replace(/^\//, '') : ''}#assertion=${encodeURIComponent(sess.assertion)}`;
      window.open(targetUrl, '_blank');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not launch restaurant session');
    } finally {
      setOpening(null);
    }
  }

  async function renewPlan(planType: 'monthly' | 'annual' = 'monthly') {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const order = await api.createPaymentOrder(restaurant.id, planType);
      
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Billdedo POS Subscription',
        description: `Renewal for ${restaurant.name} (${planType === 'annual' ? '1 Year' : '30 Days'})`,
        order_id: order.order_id,
        handler: async function (response: any) {
          setBusy(true);
          try {
            const ver = await api.verifyPayment(restaurant.id, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_type: planType,
            });
            setMsg(ver.message || 'Payment Verified! Plan successfully renewed.');
            await onChanged();
          } catch (e) {
            setErr(e instanceof Error ? e.message : 'Payment signature verification failed.');
          } finally {
            setBusy(false);
          }
        },
        modal: {
          ondismiss: function () {
            setBusy(false);
          },
        },
        prefill: {
          name: restaurant.name,
        },
        theme: {
          color: '#38bdf8',
        },
      };

      if (!(window as any).Razorpay) {
        // No silent free renewal here. This used to fall through to the
        // unpaid endpoint when the Razorpay script had not loaded, which
        // turned a blocked CDN into a free month. Extending without payment
        // is now a deliberate, Scandeer-only action — see extendWithoutPayment.
        setErr('The payment window could not load. Check the connection and try again.');
        setBusy(false);
        return;
      }
      const rzp = new (window as any).Razorpay(options);
      rzp.open();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e instanceof Error ? e.message : 'Could not initiate payment order'));
      setBusy(false);
    }
  }

  /**
   * Extend the plan with no money taken.
   *
   * Scandeer admins only, and the server enforces that — see renewPlan in
   * restaurants.ts. Confirmed first because it is a commercial decision with
   * no payment record behind it, and audited as restaurant.renew_unpaid.
   */
  async function extendWithoutPayment(planType: 'monthly' | 'annual') {
    const label = planType === 'annual' ? 'a year' : '30 days';
    if (
      !window.confirm(
        `Extend ${restaurant.name} by ${label} without taking payment?

` +
          'No money is collected and nothing is invoiced. This is recorded in the audit log.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const r = await api.renewPlan(restaurant.id, planType);
      setMsg(r.message || 'Plan extended without payment');
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not extend the plan');
    } finally {
      setBusy(false);
    }
  }

  async function toggleDelivery() {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const nextVal = !restaurant.has_delivery;
      await api.updateRestaurant(restaurant.id, { hasDelivery: nextVal });
      setMsg(nextVal ? 'Delivery feature enabled and pushed to restaurant POS!' : 'Delivery feature disabled for this restaurant.');
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not update delivery plan feature');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Switch this restaurant's printed table cards between opening the menu and
   * opening WhatsApp.
   *
   * Scandeer's call rather than the restaurant's, because the number behind
   * the WhatsApp card is Scandeer's — a restaurant switching itself onto one
   * nobody has provisioned would print cards that go nowhere.
   */
  async function toggleGuestQr() {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const next = restaurant.guest_qr === 'whatsapp' ? 'menu' : 'whatsapp';
      await api.updateRestaurant(restaurant.id, { guestQr: next });
      setMsg(
        next === 'whatsapp'
          ? 'Table codes now open WhatsApp. Reprint them from the restaurant’s Floor QR screen.'
          : 'Table codes now open the menu directly. Reprint them from the restaurant’s Floor QR screen.',
      );
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change how the table codes work');
    } finally {
      setBusy(false);
    }
  }

  async function deploy() {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const r = await api.deploy(restaurant.id);
      setMsg(`Deploy ${r.jobId} queued — this takes a few minutes.`);
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not start the deploy');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Ask the restaurant how it is, now.
   *
   * Deliberately not on `busy`: this is a read, it changes nothing at the
   * restaurant, and blocking the deploy and plan buttons behind a health probe
   * would be a worse page than one where both can be pressed.
   */
  async function checkHealth() {
    setChecking(true);
    setErr('');
    setMsg('');
    try {
      const r = await api.checkHealth(restaurant.id);
      if (!r.checked) {
        setMsg('Nothing to check yet — this restaurant has not been deployed.');
      } else if (r.health_state === 'ok') {
        setMsg(`Healthy, running v${r.reported_version}.`);
      } else {
        setMsg(`Reported ${r.health_state}: ${r.detail}`);
      }
      await onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not reach the restaurant');
    } finally {
      setChecking(false);
    }
  }

  /*
   * The cashier's whole page: a way in, and nothing to break.
   *
   * Returned before the rest of Detail rather than wrapping every panel in a
   * condition. A page assembled from twenty `!viewerIsCashier &&` guards is a
   * page where the twenty-first panel someone adds is visible by default, and
   * nobody notices until a cashier is looking at the Cloudflare token.
   */
  if (viewerIsCashier) {
    return (
      <>
        <div className="pane-head">
          <h2>{restaurant.name}</h2>
          <span className="pill">cashier</span>
          <span className="spacer" />
        </div>

        {err && <div className="banner bad">{err}</div>}

        <section className="panel" style={{ marginTop: 20 }}>
          <div className="panel-head">
            <h2>Open the POS</h2>
          </div>
          <p className="sub" style={{ margin: '4px 0 14px' }}>
            Signs you straight in to {restaurant.name} — the floor and the bill
            history. Nothing here needs a password.
          </p>
          {isDeployed ? (
            <button
              className="btn primary"
              disabled={!!opening}
              /*
               * The dashboard, not /pos.
               *
               * /pos is the counter till: a settle pane and a list of tabs,
               * for a terminal that does nothing else all day. A cashier
               * arriving from the portal is a person who signs in, works a
               * floor and looks things up, and that is the dashboard — which
               * is also where every screen built since lives.
               *
               * Safe because the dashboard restricts itself: tabsFor() gives
               * a cashier Orders and Bills and nothing more, and the server
               * refuses Reports and Settings to them regardless. This button
               * carries their own role in its assertion, so it cannot be a
               * way round either.
               */
              onClick={() => openRestaurantApp('')}
            >
              {opening ? 'Opening…' : '🧾 Open POS'}
            </button>
          ) : (
            <p className="sub" style={{ margin: 0 }}>
              This restaurant has not been set up yet. Ask the owner.
            </p>
          )}
        </section>
      </>
    );
  }

  return (
    <>
      <div className="pane-head">
        <h2>{restaurant.name}</h2>
        <StatusPill status={restaurant.status} />
        <span className="spacer" />

        {/* 1-Click Launch buttons */}
        {isDeployed && restaurant.plan_status !== 'expired' && (
          <div style={{ display: 'flex', gap: 6, marginRight: 8 }}>
            <button
              className="btn sm primary"
              disabled={!!opening}
              onClick={() => openRestaurantApp('')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', fontWeight: 650 }}
              title="1-Click Login to the restaurant's POS"
            >
              <span>⚡</span> {opening === 'dashboard' ? 'Opening…' : 'Open POS'}
            </button>
          </div>
        )}

        {/* Action Button: Deploy / Redeploy / Up to date */}
        {deploying ? (
          <button className="btn primary sm" disabled>
            Deploying…
          </button>
        ) : !isDeployed ? (
          <button
            className="btn primary sm"
            disabled={!connected || busy}
            onClick={deploy}
            title={!connected ? 'Connect a Cloudflare account first' : `Deploy v${latestVersion}`}
          >
            {busy ? 'Starting…' : `🚀 Deploy v${latestVersion}`}
          </button>
        ) : !isUpToDate ? (
          <button
            className="btn update sm"
            disabled={!connected || busy}
            onClick={deploy}
            title={`Redeploy to upgrade to v${latestVersion}`}
          >
            {busy ? 'Starting…' : `🔄 Redeploy / Update to v${latestVersion}`}
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="version-tag up-to-date" style={{ padding: '5px 10px', fontSize: 12 }}>
              ✓ Up to date (v{latestVersion})
            </span>
            <button
              className="btn sm ghost"
              disabled={!connected || busy}
              onClick={deploy}
              title="Force re-run deployment"
              style={{ fontSize: 11, opacity: 0.65 }}
            >
              {busy ? 'Starting…' : 'Force Redeploy'}
            </button>
          </div>
        )}
      </div>

      {msg && <div className="banner ok">{msg}</div>}
      {err && <div className="banner bad">{err}</div>}
      {restaurant.plan_status === 'expired' && (
        <div className="banner bad" style={{ marginTop: 12, fontSize: 13, fontWeight: 650 }}>
          🔒 <strong>Subscription / 7-Day Free Trial Expired</strong> — POS &amp; Dashboard access is locked for {restaurant.name}. Please click a renewal button below to restore access.
        </div>
      )}

      <dl className="facts">
        <Fact
          label="Plan & Status"
          value={
            /*
              What the plan IS, then what you can DO about it — on separate
              rows. Four saturated badges in a line, two of them buttons and one
              a gradient, made the plan look like an alert and gave a routine
              renewal the visual weight of a warning.
            */
            <div className="plan">
              <div className="plan-facts">
                <span className="pill plan-name">
                  {restaurant.plan_name || (restaurant.has_delivery ? 'POS + Delivery Suite' : 'Standard POS Suite')} · {restaurant.plan_price || '₹1,000/month'}
                </span>
                {/* What the restaurant actually gets. Saying "Geofencing and
                    WhatsApp ordering" to somebody on Scandeer describes a
                    product they do not have: Scandeer draws the area, sets
                    the fee and sends the rider. */}
                {restaurant.has_delivery && restaurant.delivery_provider === 'scandeer' ? (
                  <span className="pill plan-ok" title="Delivery orders arrive from Scandeer, which handles the area, the fee and the rider">
                    🛵 Scandeer Delivery
                  </span>
                ) : restaurant.has_delivery ? (
                  <span className="pill plan-ok" title="Delivery and geofencing are enabled">
                    🛵 Delivery Included
                  </span>
                ) : (
                  <span className="pill" style={{ background: 'var(--surface-2)', color: 'var(--text-3)' }} title="Delivery module is not enabled for this restaurant">
                    🛵 No Delivery
                  </span>
                )}
                {/* Only when it is on. A restaurant on the direct menu is the
                    default and the overwhelming majority, and a grey "not
                    this" pill on every row would say nothing while crowding
                    the two that matter. */}
                {restaurant.guest_qr === 'whatsapp' && (
                  <span className="pill plan-ok" title="Table codes open WhatsApp; the guest sends a message and gets the menu link back">
                    💬 WhatsApp Menu
                  </span>
                )}
                {restaurant.plan_status === 'trial' ? (
                  <span className="pill plan-ok">
                    Trial · {restaurant.days_remaining} days left
                  </span>
                ) : restaurant.plan_status === 'active' ? (
                  <span className="pill plan-ok">
                    Active · {restaurant.days_remaining} days left
                  </span>
                ) : (
                  <span className="pill plan-bad">Expired · POS locked</span>
                )}
              </div>
              <div className="plan-actions">
                <button
                  className="btn sm primary"
                  disabled={busy}
                  onClick={() => renewPlan('monthly')}
                  title="Renew for 30 days"
                >
                  Renew monthly · ₹1,000
                </button>
                <button
                  className="bar-btn"
                  disabled={busy}
                  onClick={() => renewPlan('annual')}
                  title="Renew for a year — saves ₹2,000"
                >
                  Renew annual · ₹10,000
                </button>
                {/* Scandeer staff only, and the server enforces it too: the
                    comp, the goodwill extension, the card that failed while
                    the restaurant is open for dinner. Visibly separate from
                    the two buttons that take money. */}
                {canEdit && identity.kind === 'scandeer' && identity.role === 'admin' && (
                  <>
                    <button
                      className="bar-btn"
                      disabled={busy}
                      onClick={() => void extendWithoutPayment('monthly')}
                      title="Add 30 days with no payment taken. Recorded in the audit log."
                      style={{ borderColor: 'var(--warn, #f59e0b)' }}
                    >
                      Extend 30d · no payment
                    </button>
                    <button
                      className="bar-btn"
                      disabled={busy}
                      onClick={() => void extendWithoutPayment('annual')}
                      title="Add a year with no payment taken. Recorded in the audit log."
                      style={{ borderColor: 'var(--warn, #f59e0b)' }}
                    >
                      Extend 1y · no payment
                    </button>
                  </>
                )}
                {/* Not offered while Scandeer is connected. The delivery
                    module is what permits delivery MENUS, so turning it off
                    on a connected restaurant would strand every Scandeer
                    order on a till that can no longer price it — a one-click
                    way to break a working integration, for no gain. */}
                {canEdit && restaurant.delivery_provider !== 'scandeer' && (
                  <button
                    className="bar-btn"
                    disabled={busy}
                    onClick={toggleDelivery}
                    title={restaurant.has_delivery ? 'Remove Delivery Module from plan' : 'Add Delivery Module (map zones, delivery menus) to plan'}
                    style={{ borderColor: restaurant.has_delivery ? 'var(--border)' : 'var(--accent)' }}
                  >
                    {restaurant.has_delivery ? 'Disable Delivery' : '➕ Enable Delivery Plan'}
                  </button>
                )}
                {/* Scandeer staff only: it points at a Scandeer-owned number,
                    and a reseller switching a restaurant onto one nobody has
                    provisioned is a support call for us, not for them. */}
                {canEdit && identity.kind === 'scandeer' && (
                  <button
                    className="bar-btn"
                    disabled={busy}
                    onClick={toggleGuestQr}
                    title={
                      restaurant.guest_qr === 'whatsapp'
                        ? 'Go back to table codes that open the menu in one scan'
                        : 'Table codes open WhatsApp instead: the guest sends a message and gets the menu link back — four extra taps for them, their phone number for the restaurant'
                    }
                    style={{ borderColor: restaurant.guest_qr === 'whatsapp' ? 'var(--border)' : 'var(--accent)' }}
                  >
                    {restaurant.guest_qr === 'whatsapp'
                      ? 'Guest Menu: Direct Link'
                      : '💬 Guest Menu: Use WhatsApp'}
                  </button>
                )}
              </div>
            </div>
          }
        />
        <Fact label="Short name" value={restaurant.slug} mono />
        <Fact label="Worker" value={restaurant.worker_name ?? '—'} mono />
        <Fact
          label="Cloudflare account"
          value={restaurant.cf_account_id ?? 'not connected'}
          mono
        />
        <Fact
          label="API token"
          value={
            restaurant.has_token
              ? `••••${restaurant.token_last4} · verified ${timeAgo(restaurant.token_verified_at)}`
              : 'none'
          }
        />
        <Fact
          label="Dashboard"
          value={
            restaurant.base_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn sm primary"
                  disabled={!!opening}
                  onClick={() => openRestaurantApp('')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', fontSize: 12 }}
                >
                  <span>⚡</span> {opening === 'dashboard' ? 'Logging in…' : '1-Click Login (SSO)'}
                </button>
                <a
                  href={restaurant.base_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 12, color: 'var(--text-2)', textDecoration: 'underline' }}
                  title="Direct URL (Password Login)"
                >
                  {restaurant.base_url}
                </a>
                {/* The Windows till application. Served from R2 by the portal
                    itself — 80 MB is far past what a static asset may be — and
                    offered here because this is the page somebody is on when
                    they are setting a restaurant up. */}
                <a
                  href={WINDOWS_INSTALLER_URL}
                  className="btn sm"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    fontSize: 12,
                    textDecoration: 'none',
                  }}
                  title={`Download the Windows desktop POS installer (v${WINDOWS_INSTALLER_VERSION})`}
                >
                  <span>⬇️</span> Desktop POS (Windows)
                </a>
                {/* The Android terminal — a tablet till or a phone — from Google Play. */}
                <a
                  href={ANDROID_PLAY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn sm"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 10px',
                    fontSize: 12,
                    textDecoration: 'none',
                  }}
                  title="Install the Android POS from Google Play"
                >
                  <span>▶</span> Android POS (Google Play)
                </a>
              </div>
            ) : (
              'not deployed'
            )
          }
        />
        <Fact
          label="Running Version"
          value={
            runningLabel ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong
                  style={{ fontFamily: 'var(--mono)' }}
                  title={
                    running.source === 'reported'
                      ? 'Reported by the restaurant itself'
                      : 'Recorded by the deploy pipeline — not confirmed by the restaurant'
                  }
                >
                  v{runningLabel}
                </strong>
                {isUpToDate ? (
                  <span className="version-tag up-to-date">✓ Up to date</span>
                ) : (
                  <span className="version-tag update-available">
                    ⚠️ Update Available (v{latestVersion})
                  </span>
                )}
                {/* Our bookkeeping disagrees with the running code. The
                    restaurant is fine; the deploy record is stale, and the
                    next successful deploy re-stamps it. Saying so here is
                    what stops it reading as "the update failed". */}
                {running.drifted && (
                  <span className="meta" style={{ margin: 0, fontSize: 11 }}>
                    deploy record says v{running.recorded}
                  </span>
                )}
              </span>
            ) : (
              'Not deployed yet'
            )
          }
        />
        <Fact
          label="Latest Available"
          value={<strong style={{ fontFamily: 'var(--mono)', color: 'var(--accent, #38bdf8)' }}>v{latestVersion}</strong>}
        />
        <Fact
          label="Health"
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>
                {restaurant.health_state} · {timeAgo(restaurant.health_checked_at)}
              </span>
              {/* The cron sweeps every fifteen minutes; this is for the person
                  who has just deployed and wants the answer now. */}
              {!!restaurant.live_at && (
                <button className="btn sm ghost" onClick={checkHealth} disabled={checking}>
                  {checking ? 'Checking…' : 'Check now'}
                </button>
              )}
            </span>
          }
        />
        <Fact label="Channel" value={restaurant.channel} />
      </dl>

      <ConnectPanel restaurant={restaurant} onConnected={onChanged} />
      {/* Not the panel that was removed in 4fae4fa, although it is the same
          file name. That one configured a Meta number for WhatsApp DELIVERY
          ordering, which Scandeer owns, so it set up three things for orders
          arriving through none of them. This one is the guest menu: a number
          a restaurant's own diners message for a menu link, which is a real
          use for a number of its own. Mounted for every restaurant, since the
          guest menu has nothing to do with who delivers. */}
      <WhatsAppConnectPanel restaurant={restaurant} onConnected={onChanged} />
      <ScandeerConnectPanel restaurant={restaurant} onConnected={onChanged} />

      <MembersPanel restaurantId={restaurant.id} onError={setErr} />

      <DeployHistory restaurantId={restaurant.id} live={deploying} />

      {/*
        On the restaurant's page because this is where somebody stands when
        the question arrives — the token is refused, the deploy failed, the
        till is printing the wrong thing. Part 5 of the guide is those
        questions.
      */}
      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-head">
          <h2>Setup guide</h2>
        </div>
        <p className="sub" style={{ margin: '4px 0 10px' }}>
          Connecting Cloudflare, deploying, configuring the till and installing
          the POS on the counter — including what to do when a deploy fails or a
          change does not reach the printers.
        </p>
        <SetupGuideLink />
      </section>

      <section className="panel" style={{ marginTop: 24 }}>
        <div className="panel-head">
          <h2>Install the POS</h2>
        </div>
        <p className="sub" style={{ margin: '4px 0 10px' }}>
          On the counter PC or a tablet till. Both sign in with Google or WhatsApp,
          then list your restaurants — pick this one and it opens.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a className="btn" href={WINDOWS_INSTALLER_URL} style={{ textDecoration: 'none' }}>
            ⬇️ Windows — Billdedo Setup {WINDOWS_INSTALLER_VERSION}
          </a>
          <a
            className="btn"
            href={ANDROID_PLAY_URL}
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            ▶ Android — Google Play
          </a>
        </div>
      </section>

      {/*
        Last on the page, and alone, because everything above it is routine
        and this is not. A destructive action sitting next to Deploy is a
        destructive action somebody eventually reaches for instead.
      */}
      {identity.kind === 'scandeer' && identity.role === 'admin' && (
        <section className="panel" style={{ marginTop: 24, borderColor: '#7f1d1d' }}>
          <div className="panel-head">
            <h2 style={{ color: '#f87171' }}>Clear data</h2>
          </div>
          <p className="sub" style={{ marginBottom: 12 }}>
            Deletes every bill, payment, delivery order and open tab at{' '}
            <strong>{restaurant.name}</strong>. The menu, prices, staff, tables,
            printers and settings all stay. There is no undo.
          </p>
          {/*
            Typed, not a prompt(). On a page listing every restaurant the
            mistake that actually happens is the wrong card, and a yes/no box
            catches none of it — you have to name the one you mean. An inline
            field rather than window.prompt because Electron does not
            implement prompt at all, and this app is also opened there.
          */}
          <input
            className="input"
            value={clearConfirm}
            onChange={(e) => setClearConfirm(e.target.value)}
            placeholder={`Type "${restaurant.name}" to enable`}
            style={{ marginBottom: 10, maxWidth: 360 }}
            aria-label="Confirm the restaurant name"
          />
          <button
            className="btn sm"
            style={{ background: '#7f1d1d', color: '#fff', borderColor: '#7f1d1d' }}
            disabled={
              busy || !restaurant.base_url || clearConfirm.trim() !== restaurant.name.trim()
            }
            title={
              !restaurant.base_url
                ? 'Deploy the restaurant first'
                : clearConfirm.trim() !== restaurant.name.trim()
                  ? 'Type the restaurant name above to enable this'
                  : 'Delete all trade for this restaurant'
            }
            onClick={async () => {
              const warned = window.confirm(
                `Delete ALL trade for ${restaurant.name}?\n\n` +
                  'Bills, payments, delivery orders and open tabs will be deleted.\n' +
                  'The menu, staff, tables and printers are kept.\n\n' +
                  'This cannot be undone.',
              );
              if (!warned) return;

              setBusy(true);
              setErr('');
              setMsg('');
              try {
                const r = await api.resetRestaurant(restaurant.id);
                const total = Object.values(r.cleared).reduce((a, b) => a + b, 0);
                setMsg(`${r.restaurant} cleared — ${total} record(s) deleted.`);
                setClearConfirm('');
                await onChanged();
              } catch (e) {
                setErr(e instanceof ApiError ? e.message : 'Could not clear this restaurant');
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Clearing…' : `Clear all data for ${restaurant.name}`}
          </button>

          {/* The menu, which the wipe above says it keeps.
              A second button rather than a checkbox on the first: the promise
              that "the menu stays" is worth more than one less control, and a
              Scandeer import that came in wrong is the reason this exists —
              91 dishes and 23 categories are not removable by hand. */}
          <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #7f1d1d' }} />
          <p className="sub" style={{ marginBottom: 10 }}>
            Or delete the <strong>menu</strong> — every category, dish, variant and menu, and the
            links to Scandeer. Bills already rung keep their own copy of what was sold, so trade
            history is unaffected. Staff, tables, printers and settings stay.
          </p>
          <button
            className="btn sm"
            style={{ background: '#7f1d1d', color: '#fff', borderColor: '#7f1d1d' }}
            disabled={
              busy || !restaurant.base_url || clearConfirm.trim() !== restaurant.name.trim()
            }
            title={
              !restaurant.base_url
                ? 'Deploy the restaurant first'
                : clearConfirm.trim() !== restaurant.name.trim()
                  ? 'Type the restaurant name above to enable this'
                  : 'Delete the whole menu for this restaurant'
            }
            onClick={async () => {
              const warned = window.confirm(
                `Delete the ENTIRE MENU for ${restaurant.name}?` +
                  '\n\nEvery category, dish, variant, modifier and menu will be' +
                  ' deleted, along with the links to Scandeer.' +
                  '\n\nBills already rung are unaffected. This cannot be undone.',
              );
              if (!warned) return;

              setBusy(true);
              setErr('');
              setMsg('');
              try {
                const r = await api.resetRestaurantMenu(restaurant.id);
                const total = Object.values(r.cleared).reduce((a, b) => a + b, 0);
                setMsg(`${r.restaurant}: menu deleted — ${total} record(s) removed.`);
                setClearConfirm('');
                await onChanged();
              } catch (e) {
                setErr(e instanceof ApiError ? e.message : 'Could not clear this menu');
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Clearing…' : `Delete the menu for ${restaurant.name}`}
          </button>
        </section>
      )}

      {/*
        Only on a restaurant with nothing behind it.

        Hidden rather than shown-and-disabled on the rest: a delete button on a
        live restaurant is a thing somebody tries, and the explanation for why
        it will not work belongs in the one place it can actually be read —
        the 409 from the server, if anyone reaches it.
      */}
      {identity.kind === 'scandeer' &&
        identity.role === 'admin' &&
        !connected &&
        !restaurant.cf_account_id &&
        !restaurant.base_url &&
        !restaurant.deployed_version && (
          <section className="panel" style={{ marginTop: 24, borderColor: '#7f1d1d' }}>
            <div className="panel-head">
              <h2 style={{ color: '#f87171' }}>Delete restaurant</h2>
            </div>
            <p className="sub" style={{ marginBottom: 12 }}>
              Removes <strong>{restaurant.name}</strong> from the portal. Nothing
              has been deployed for it, so there is nothing else to remove — no
              Worker, no database, no storage. The audit trail is kept. There is
              no undo.
            </p>
            <input
              className="input"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder={`Type "${restaurant.name}" to enable`}
              style={{ marginBottom: 10, maxWidth: 360 }}
              aria-label="Confirm the restaurant name to delete it"
            />
            <button
              className="btn sm"
              style={{ background: '#7f1d1d', color: '#fff', borderColor: '#7f1d1d' }}
              disabled={busy || deleteConfirm.trim() !== restaurant.name.trim()}
              title={
                deleteConfirm.trim() !== restaurant.name.trim()
                  ? 'Type the restaurant name above to enable this'
                  : 'Remove this restaurant from the portal'
              }
              onClick={async () => {
                const warned = window.confirm(
                  `Delete ${restaurant.name} from the portal?\n\n` +
                    'Nothing has been deployed for it, so only the portal record goes.\n\n' +
                    'This cannot be undone.',
                );
                if (!warned) return;

                setBusy(true);
                setErr('');
                setMsg('');
                try {
                  await api.deleteRestaurant(restaurant.id);
                  setDeleteConfirm('');
                  // Clears the pane as well as the list: the record this is
                  // rendering no longer exists.
                  onDeleted();
                  await onChanged();
                } catch (e) {
                  setErr(e instanceof ApiError ? e.message : 'Could not delete this restaurant');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Deleting…' : `Delete ${restaurant.name}`}
            </button>
          </section>
        )}
    </>
  );
}

function Fact({
  label,
  value,
  mono,
  link,
  warn,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  link?: boolean;
  warn?: boolean;
}) {
  const isString = typeof value === 'string';
  return (
    <>
      <dt>{label}</dt>
      <dd className={`${mono ? 'mono' : ''} ${warn ? 'warn' : ''}`}>
        {link && isString && (value as string).startsWith('http') ? (
          <a href={value as string} target="_blank" rel="noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </>
  );
}

// ---------------------------------------------------------------------------

function AddRestaurant({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [hasDelivery, setHasDelivery] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Suggest the short name from the restaurant name so the common case needs
  // no thought. It becomes the Worker name, so it cannot be changed later
  // without redeploying — worth getting right at creation.
  useEffect(() => {
    const suggested = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
    setSlug((s) => (s === '' || suggested.startsWith(s) || s.startsWith(suggested) ? suggested : s));
  }, [name]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await api.createRestaurant({
        name: name.trim(),
        slug,
        ...(contactEmail ? { contactEmail } : {}),
        hasDelivery: hasDelivery ? 1 : 0,
      });
      onAdded(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the restaurant');
      setBusy(false);
    }
  }

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="card drawer" onSubmit={submit}>
        <h2>New restaurant</h2>

        {/*
          Before the first field, not after the last.
          
          Two of the four answers below are permanent — the short name becomes
          the Worker name and cannot be changed after the first deploy, and the
          Cloudflare account has to be the restaurant's own, created in their
          name. Both are cheap to get right here and expensive to undo later,
          and the guide is where they are explained.
        */}
        <div className="banner warn" style={{ marginTop: 4 }}>
          <strong>New to this?</strong> The short name and the Cloudflare account
          are hard to change later. <SetupGuideLink /> first — it covers both,
          and the whole deploy.
        </div>

        {error && <div className="banner bad">{error}</div>}

        <label htmlFor="n">Name</label>
        <input id="n" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />

        <label htmlFor="s">Short name</label>
        <input
          id="s"
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          spellCheck={false}
          required
        />
        <div className="hint">
          Becomes the Worker name — <span className="mono">billdedo-{slug || 'name'}</span>. Lowercase
          letters, numbers and dashes. It cannot be changed after the first deploy.
        </div>

        <label htmlFor="c">Owner email</label>
        <input
          id="c"
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
        <div className="hint">
          The Cloudflare account must be created by the restaurant, in their name.
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, cursor: 'pointer', fontWeight: 600 }}>
          <input
            type="checkbox"
            checked={hasDelivery}
            onChange={(e) => setHasDelivery(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: '#f59e0b' }}
          />
          <span>🛵 Include Delivery & Map Zones Module</span>
        </label>
        <div className="hint" style={{ marginTop: 2 }}>
          {hasDelivery
            ? 'Plan: All-in-One POS + Delivery Suite (₹1,000/month)'
            : 'Plan: Standard POS Suite (₹1,000/month)'}
        </div>

        <div className="actions">
          <button className="btn primary" disabled={busy}>
            {busy ? 'Adding…' : 'Add restaurant'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function UsersDialog({
  onClose,
  currentUserId,
}: {
  onClose: () => void;
  currentUserId: string;
}) {
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.users();
      setUsers(res.users);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load users');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function updateRole(userId: string, roleType: 'super_admin' | 'admin' | 'user') {
    setBusyId(userId);
    setError('');
    try {
      await api.updateUser(userId, { roleType });
      setToast('Role updated successfully');
      setTimeout(() => setToast(''), 2500);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update user role');
    } finally {
      setBusyId(null);
    }
  }

  async function toggleActive(userId: string, currentActive: number) {
    setBusyId(userId);
    setError('');
    try {
      await api.updateUser(userId, { active: currentActive === 1 ? false : true });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update user status');
    } finally {
      setBusyId(null);
    }
  }

  const getUserRoleType = (u: PortalUser): 'super_admin' | 'admin' | 'user' => {
    if (u.kind === 'scandeer' && u.role === 'admin') return 'super_admin';
    if (u.kind === 'scandeer' || u.kind === 'reseller') return 'admin';
    return 'user';
  };

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card drawer" style={{ maxWidth: 720 }} role="dialog" aria-label="Manage users">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>👥 Portal Users & Roles</h2>
          <span className="spacer" />
          <button className="btn sm ghost" onClick={onClose}>✕</button>
        </div>

        {error && <div className="banner bad">{error}</div>}
        {toast && <div className="banner ok">{toast}</div>}

        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table className="perms" style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const currentType = getUserRoleType(u);
                const isSelf = u.id === currentUserId;
                const isBusy = busyId === u.id;

                return (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      {isSelf && <span className="hint">(You)</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>{u.email}</td>
                    <td>
                      <select
                        value={currentType}
                        disabled={isBusy || isSelf}
                        onChange={(e) => void updateRole(u.id, e.target.value as any)}
                        style={{
                          padding: '4px 8px',
                          fontSize: 12.5,
                          borderRadius: 6,
                          background: 'var(--panel-2)',
                          color: 'var(--text)',
                          border: '1px solid var(--line)',
                        }}
                      >
                        <option value="super_admin">⚡ Super Admin (All Restaurants)</option>
                        <option value="admin">🛡️ Admin (Fleet/Reseller)</option>
                        <option value="user">👤 User (Restaurant Owner)</option>
                      </select>
                    </td>
                    <td>
                      <button
                        className={`btn sm ${u.active === 1 ? 'ghost' : ''}`}
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          color: u.active === 1 ? 'var(--ok)' : 'var(--danger)',
                        }}
                        disabled={isBusy || isSelf}
                        onClick={() => void toggleActive(u.id, u.active)}
                      >
                        {u.active === 1 ? 'Active' : 'Deactivated'}
                      </button>
                    </td>
                    <td className="hint" style={{ fontSize: 11.5 }}>
                      {timeAgo(u.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="actions" style={{ marginTop: 18 }}>
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentSettingsDialog({ onClose }: { onClose: () => void }) {
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [secretSet, setSecretSet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    api
      .getAdminSettings()
      .then((res) => {
        setKeyId(res.razorpay_key_id || '');
        setSecretSet(res.razorpay_key_secret_set);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not load payment settings');
      });
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setToast('');
    try {
      const res = await api.saveAdminSettings({
        razorpay_key_id: keyId,
        razorpay_key_secret: keySecret ? keySecret : undefined,
      });
      setToast(res.message || 'Settings saved successfully');
      setSecretSet(secretSet || !!keySecret);
      setKeySecret('');
      setTimeout(() => setToast(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save payment settings');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card drawer" style={{ maxWidth: 520 }} role="dialog" aria-label="Payment Gateway Settings">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>💳 Razorpay Payment Gateway Settings</h2>
          <span className="spacer" />
          <button className="btn sm ghost" onClick={onClose}>✕</button>
        </div>

        <p className="hint" style={{ fontSize: 13, margin: '0 0 16px 0', lineHeight: 1.4 }}>
          Super Admin Setup: Enter your Razorpay API Credentials below. You can easily switch between <strong>Test Mode Keys (rzp_test_...)</strong> and <strong>Live Production Keys (rzp_live_...)</strong> at any time.
        </p>

        {error && <div className="banner bad">{error}</div>}
        {toast && <div className="banner ok">{toast}</div>}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              Razorpay Key ID (Public Key):
            </label>
            <input
              type="text"
              placeholder="e.g. rzp_test_xxxxxxxxxxxx or rzp_live_xxxxxxxxxxxx"
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 6, background: 'var(--panel-2)', border: '1px solid var(--line)', color: '#fff' }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
              Razorpay Key Secret:
            </label>
            <input
              type="password"
              placeholder={secretSet ? '•••••••••••• (Leave blank to keep existing)' : 'Enter Secret Key'}
              value={keySecret}
              onChange={(e) => setKeySecret(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 6, background: 'var(--panel-2)', border: '1px solid var(--line)', color: '#fff' }}
            />
            {secretSet && (
              <span className="hint" style={{ fontSize: 11, color: '#22c55e', display: 'block', marginTop: 4 }}>
                ✓ Secret Key is currently configured securely in database.
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button className="btn primary sm" disabled={busy} style={{ fontWeight: 700, padding: '8px 16px' }}>
              {busy ? 'Saving…' : '💾 Save Gateway Keys'}
            </button>
            <button type="button" className="btn sm ghost" onClick={onClose}>
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
